import { EntityState } from "@reduxjs/toolkit";
import { Vector3D } from "common/types";
import { AssetModel, MapModel } from "common/model";

// AI マップ生成データ（api-contract.md）
export interface GeneratedMapData {
  tiles: Array<{ x: number; z: number; name: string }>;
  character: { x: number; z: number };
  goal: { x: number; z: number };
  gems: Array<{ x: number; z: number }>;
  decorations: Array<{ x: number; z: number; name: string }>;
}

export type BuilderMap = {
  id: string;
  inquiry_id: string;
  author_uid: string;
  author_name: string;
  author_icon: string;
  name: string;
  version: number;
  description: string;
  capture?: string;
  cover?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
};

// マップ難易度（AI 生成）
export enum Difficulty {
  EASY = "easy",
  MEDIUM = "medium",
  HARD = "hard",
}

// ピースのライフサイクル状態（spec.md §3 状態遷移）
export enum PieceStatus {
  DRAFT_NEW = "draft_new",
  PUBLISHED = "published",
  EDITING = "editing",
  UNPUBLISHED = "unpublished",
  REMOVED = "removed",
}

// Blockly ブロック種別（spec.md §5.8.7）
export enum BuilderBlockId {
  MOVE_FORWARD = "move_forward",
  TURN_LEFT = "turn_left",
  TURN_RIGHT = "turn_right",
  JUMP = "jump",
  LOOP = "loop",
  IF_COLOR = "if_color",
  WHILE_COLOR = "while_color",
}

// 新規ピース作成時のデフォルト有効ブロック（spec.md §5.8.7）
export const DEFAULT_ENABLED_BLOCKS: BuilderBlockId[] = [
  BuilderBlockId.MOVE_FORWARD,
  BuilderBlockId.TURN_LEFT,
  BuilderBlockId.TURN_RIGHT,
  BuilderBlockId.JUMP,
];

// 完成ステップ（spec.md §5.8.3 5/5 判定）
export enum CompletionStepId {
  CHARACTER = "character",
  GOAL = "goal",
  BLOCKS = "blocks",
  TESTED = "tested",
  META = "meta",
}

// AI 生成マップ形状（api-contract.md）。既存 GeneratedMapData と互換維持
export type GeneratedPieceMap = GeneratedMapData;

// 編集履歴エントリ（spec.md §5.8.4）
export interface PieceEditHistoryEntry {
  seq: number;
  version: number;
  action_type: string;
  payload?: Record<string, string | number | boolean>;
  created_at: string;
}

export enum PieceAiRole {
  USER = "user",
  ASSISTANT = "assistant",
}

// AI チャットメッセージ（api-contract.md）
export interface PieceAiMessage {
  id: string;
  session_id: string;
  role: PieceAiRole;
  content: string;
  map: GeneratedPieceMap | null;
  created_at: string;
}

// AI セッション（api-contract.md）
export interface PieceAiSession {
  session_id: string;
  piece_id: string;
  response_id?: string;
  created_at: string;
}

export type BuilderState = {
  selectedMap: BuilderMap | null;
  maps: EntityState<BuilderMap>;
  loading: boolean;
  editingBuilderMapId: string;
  size: Vector3D;
  map: EntityState<MapModel>;
  slot: AssetModel[];
  undoStacks: ActionRecord[];
  redoStacks: ActionRecord[];
  actionMenu: {
    tap: boolean;
    asset: boolean;
    eraser: boolean;
    assetList: boolean;
  };
  selectedSlotIndex: number | null;
  selectedAssetModel: AssetModel | null;
  selectedPlacedModelId: string | null;
  // ピースビルダー: 完成判定・ドラフト編集（spec.md §5.8）
  name: string;
  description: string;
  enabledBlocks: BuilderBlockId[];
  tested: boolean;
  status: PieceStatus;
  editHistory: PieceEditHistoryEntry[];
  draftDirty: boolean;
};

export enum ActionCommandType {
  ADD_MODE = "ADD_MODE",
  REMOVE_MODE = "REMOVE_MODE",
  CHANGE_MODE = "CHANGE_MODE",
  UNDO = "UNDO",
  REDO = "REDO",
  CLEAR = "CLEAR",
}

// Undo/Redo用のアクションペイロード
export interface ActionPayload {
  model?: MapModel;
  meshId?: string;
  id?: string;
  changes?: { [key: string]: string | Vector3D };
}

// アクション履歴レコード
export interface ActionRecord {
  type: ActionCommandType;
  forward: ActionPayload;
  backward: ActionPayload;
  timestamp: number;
}

export interface AddModeCommand {
  type: ActionCommandType.ADD_MODE;
  model: MapModel;
}

export interface RemoveModeCommand {
  type: ActionCommandType.REMOVE_MODE;
  meshId: string;
}

export interface ChangeModeCommand {
  type: ActionCommandType.CHANGE_MODE;
  id: string;
  changes: {
    [item: string]: string | Vector3D;
  };
}

export interface UndoCommand {
  type: ActionCommandType.UNDO;
}

export interface RedoCommand {
  type: ActionCommandType.REDO;
}

export interface ClearCommand {
  type: ActionCommandType.CLEAR;
}

export type ActionCommand =
  | AddModeCommand
  | RemoveModeCommand
  | ChangeModeCommand
  | UndoCommand
  | RedoCommand
  | ClearCommand;
